/**
 * HealthHeader Component
 * Header with overall status, version, environment, and refresh controls
 */

import React, { useMemo } from 'react';
import { Button } from 'semantic-ui-react';
import { RefreshCw01, RefreshCcw01 } from '@untitledui/icons';
import { I18n } from 'react-redux-i18n';
import { HealthData, HealthStatus } from '../types';
import { calculateOverallStatus } from '../helpers/statusCalculators';
import StatusDot from './StatusDot';
import { useVersionCheck } from '../hooks/useVersionCheck';
import styles from '../SystemHealth.module.css';

const I18N_PREFIX = 'systemSettings.systemHealth';

interface HealthHeaderProps {
  data: HealthData | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  lastUpdated: Date | null;
  secondsUntilRefresh: number;
  onRefresh: () => void;
}

const formatCountdown = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatLastUpdated = (date: Date | null): string => {
  if (!date) return I18n.t(`${I18N_PREFIX}.time.never`);
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 5) return I18n.t(`${I18N_PREFIX}.time.justNow`);
  if (seconds < 60) return I18n.t(`${I18N_PREFIX}.time.secondsAgo`, { seconds });
  const minutes = Math.floor(seconds / 60);
  return I18n.t(`${I18N_PREFIX}.time.minutesAgo`, { minutes });
};

const adminVersion = import.meta.env.VITE_APP_VERSION;

export const HealthHeader: React.FC<HealthHeaderProps> = ({
  data,
  isLoading,
  isRefreshing,
  error,
  lastUpdated,
  secondsUntilRefresh,
  onRefresh
}) => {
  const { isNewVersionAvailable, serverVersion } = useVersionCheck();
  
  const overallStatus: HealthStatus = useMemo(() => {
    if (isLoading && !data) return 'loading';
    if (!data) return 'error';
    return calculateOverallStatus(data.dependencies, data.events);
  }, [data, isLoading]);

  const lastUpdatedText = useMemo(() => {
    return formatLastUpdated(lastUpdated);
  }, [lastUpdated, secondsUntilRefresh]); // Update when countdown ticks

  const handleRefreshBrowser = () => {
    window.location.reload();
  };

  return (
    <div className={styles.header}>
      {/* New version available banner */}
      {isNewVersionAvailable && (
        <div className={styles.updateBanner}>
          <div className={styles.updateBannerContent}>
            <RefreshCcw01 size={16} />
            <span>
              {I18n.t(`${I18N_PREFIX}.update.newVersionAvailable`, { 
                version: serverVersion?.version 
              })}
            </span>
          </div>
          <Button 
            size="tiny" 
            primary 
            onClick={handleRefreshBrowser}
            className={styles.updateButton}
          >
            {I18n.t(`${I18N_PREFIX}.update.refreshNow`)}
          </Button>
        </div>
      )}
      <div className={styles.headerLeft}>
        <h2 className={styles.headerTitle}>{I18n.t(`${I18N_PREFIX}.title`)}</h2>
        <span className={styles.headerSubtitle}>
          {I18n.t(`${I18N_PREFIX}.lastUpdated`)} {lastUpdatedText}
        </span>
      </div>

      <div className={styles.headerRight}>
        {/* Overall status */}
        <div className={`${styles.overallStatus} ${styles[`overall${overallStatus.charAt(0).toUpperCase() + overallStatus.slice(1)}`]}`}>
          <StatusDot status={overallStatus} size="small" />
          <span>{I18n.t(`${I18N_PREFIX}.status.${overallStatus}`)}</span>
        </div>

        {/* Admin version badge */}
        {adminVersion && (
          <span className={styles.badge} title={I18n.t(`${I18N_PREFIX}.versions.adminTitle`)}>
            {I18n.t(`${I18N_PREFIX}.versions.adminLabel`)} {adminVersion}
          </span>
        )}

        {/* API version badge */}
        {data?.version && (
          <span className={styles.badge} title={I18n.t(`${I18N_PREFIX}.versions.apiTitle`)}>
            {I18n.t(`${I18N_PREFIX}.versions.apiLabel`)} {data.version}
          </span>
        )}

        {/* Environment badge */}
        {data?.environment && (
          <span className={`${styles.badge} ${styles.envBadge}`}>
            {data.environment}
          </span>
        )}

        {/* Refresh button */}
        <Button
          icon
          className={`${styles.refreshButton} ${isRefreshing ? styles.spinning : ''}`}
          onClick={onRefresh}
          disabled={isRefreshing}
          title={I18n.t(`${I18N_PREFIX}.refresh.buttonTitle`, { countdown: formatCountdown(secondsUntilRefresh) })}
        >
          <RefreshCw01 size={16} />
          <span className={styles.refreshCountdown}>
            {formatCountdown(secondsUntilRefresh)}
          </span>
        </Button>
      </div>

      {/* Error banner */}
      {error && (
        <div className={styles.errorBanner}>
          <span>{I18n.t(`${I18N_PREFIX}.error.failedToFetch`)} {error}</span>
          <span className={styles.errorNote}>{I18n.t(`${I18N_PREFIX}.error.showingLastKnown`)}</span>
        </div>
      )}
    </div>
  );
};

export default HealthHeader;
